// Whether a tutorial chapter is open (B-527), read where a league is chosen
// (`useTutorialLeague`), so the tour's leagues are the simulator's while a
// chapter is walked and nobody's real league is touched once it is not.
//
// HELD ABOVE THE CHAT DRAWER, which reads a league too and sits outside the
// tour's own provider: the tour says what it has open (`useSetChapterOpen`),
// and the first value is the chapter the tab has stored, so a reload inside a
// chapter never reads the real league first.

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { storageKey } from './storage';

/** Where the tour keeps the chapter being walked (`TutorialProvider`). */
export const CHAPTER_KEY = storageKey('tutorial.at.v1');

const ChapterContext = createContext<{ open: boolean; setOpen: (open: boolean) => void } | null>(
  null,
);

function storedOpen(): boolean {
  try {
    const path = window.location.pathname;
    // The front door is leaving the tour, as `TutorialProvider` reads it.
    if (path === '/' || path === '/welcome') return false;
    return sessionStorage.getItem(CHAPTER_KEY) !== null;
  } catch {
    return false;
  }
}

export function TutorialChapterProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(storedOpen);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return <ChapterContext.Provider value={value}>{children}</ChapterContext.Provider>;
}

export function useChapterOpen(): boolean {
  return useContext(ChapterContext)?.open ?? false;
}

const ignore = () => undefined;

export function useSetChapterOpen(): (open: boolean) => void {
  return useContext(ChapterContext)?.setOpen ?? ignore;
}
